import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Filter, CheckCircle2, FileText, ExternalLink, ArrowLeft, TrendingUp, Award, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import DataFreshnessIndicator from "@/components/DataFreshnessIndicator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ComplianceHub({ user, logout }) {
  const navigate = useNavigate();
  const [standards, setStandards] = useState([]);
  const [filteredStandards, setFilteredStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [industryFilter, setIndustryFilter] = useState("all");

  useEffect(() => {
    fetchStandards();
  }, []);

  useEffect(() => {
    filterStandards();
  }, [searchQuery, industryFilter, standards]);

  const fetchStandards = async () => {
    try {
      const token = localStorage.getItem("standari_token");
      const response = await axios.get(`${API}/compliance/standards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStandards(response.data.standards);
      setFilteredStandards(response.data.standards);
    } catch (error) {
      console.error("Failed to load standards");
    } finally {
      setLoading(false);
    }
  };

  const filterStandards = () => {
    let filtered = standards;

    if (searchQuery) {
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.industry.some(i => i.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (industryFilter !== "all") {
      filtered = filtered.filter(s => s.industry.includes(industryFilter));
    }

    setFilteredStandards(filtered);
  };

  const getIndustryIcon = (industry) => {
    const icons = {
      "Healthcare": "🏥",
      "Aviation": "✈️",
      "Government": "🏛️",
      "Manufacturing": "🏭",
      "Financial": "💰",
      "Energy": "⚡",
      "All": "🌐"
    };
    return icons[industry] || "📄";
  };

  const industries = ["all", "Healthcare", "Aviation", "Government", "Manufacturing", "Financial", "Energy"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100" data-testid="compliance-hub">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center space-x-3">
                <svg className="h-8 w-8" viewBox="0 0 40 40" fill="none">
                  <path d="M20 2L37 12L37 28L20 38L3 28L3 12L20 2Z" fill="url(#logo-gradient)" stroke="#1e40af" strokeWidth="1.5"/>
                  <circle cx="20" cy="20" r="2.5" fill="white"/>
                  <circle cx="20" cy="12" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="28" cy="16" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="28" cy="24" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="20" cy="28" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="12" cy="24" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="12" cy="16" r="1.2" fill="white" opacity="0.7"/>
                  <defs>
                    <linearGradient id="logo-gradient" x1="0" y1="0" x2="40" y2="40">
                      <stop offset="0%" stopColor="#1e40af"/>
                      <stop offset="50%" stopColor="#3b82f6"/>
                      <stop offset="100%" stopColor="#1e3a8a"/>
                    </linearGradient>
                  </defs>
                </svg>
                <span className="text-xl font-heading font-bold text-slate-900">Standards Reference</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-blue-100 text-blue-900 px-4 py-2">
            <Award className="h-4 w-4 inline mr-2" />
            100+ Global Standards Reference Database
          </Badge>
          <h1 className="text-4xl font-heading font-bold text-slate-900 mb-4">
            Standards Database
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            ISO/IEC, HL7 FHIR, NIST, FedRAMP, ATA iSpec, GDPR, PCI-DSS, and more. 
            Upload documents to find matching standards. Export documentation assistance files.
          </p>
        </div>

        {/* Legal Disclaimer */}
        <DisclaimerBanner />

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-2 border-slate-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold font-heading text-blue-600 mb-2">{standards.length}+</div>
              <p className="text-sm text-slate-600">Standards Available</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-slate-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold font-heading text-green-600 mb-2">8</div>
              <p className="text-sm text-slate-600">Industries Covered</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-slate-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold font-heading text-purple-600 mb-2">Instant</div>
              <p className="text-sm text-slate-600">Match on Upload</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-slate-200">
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold font-heading text-orange-600 mb-2">Weekly</div>
              <p className="text-sm text-slate-600">Database Updates</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  placeholder="Search standards by name, code, or industry..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="standards-search"
                />
              </div>
              <Select value={industryFilter} onValueChange={setIndustryFilter}>
                <SelectTrigger className="w-full md:w-48" data-testid="industry-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {industries.map(industry => (
                    <SelectItem key={industry} value={industry}>
                      {industry === "all" ? "All Industries" : industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Standards Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">Loading standards database...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStandards.map((standard) => (
              <Card key={standard.id} className="hover:shadow-lg transition-all border-2 border-slate-200 hover:border-blue-400" data-testid={`standard-${standard.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className="text-xs">
                      {standard.code}
                    </Badge>
                    {standard.mandatory && (
                      <Badge className="bg-red-100 text-red-800 text-xs">Required</Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg font-heading">{standard.name}</CardTitle>
                  {standard.revision_date && (
                    <div className="mt-1">
                      <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        {standard.revision} • Updated {standard.revision_date}
                      </Badge>
                    </div>
                  )}
                  <CardDescription className="flex flex-wrap gap-1 mt-2">
                    {standard.industry.map((ind, idx) => (
                      <span key={idx} className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {getIndustryIcon(ind)} {ind}
                      </span>
                    ))}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    {standard.certification_body && (
                      <div className="flex items-center justify-between text-slate-600">
                        <span>Certifying Body:</span>
                        <span className="font-semibold text-xs">{standard.certification_body}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-slate-600">
                      <span>Data Verification:</span>
                      <DataFreshnessIndicator lastUpdated={standard.last_updated} label="Verified" />
                    </div>
                    {standard.source_url && (
                      <a 
                        href={standard.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Official Documentation
                      </a>
                    )}
                  </div>
                  <Button className="w-full mt-4" variant="outline" onClick={() => navigate(`/process?standard=${standard.code}`)}>
                    Use This Standard
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {filteredStandards.length === 0 && !loading && (
          <Card className="border-2 border-slate-200">
            <CardContent className="py-12 text-center">
              <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No standards found</h3>
              <p className="text-slate-600 mb-4">Try adjusting your search or filters</p>
              <Button onClick={() => { setSearchQuery(""); setIndustryFilter("all"); }}>
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
